import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import axios from "axios";
import { Typewriter } from "react-simple-typewriter";

const API_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:8000"
    : process.env.REACT_APP_API_URL;

// Sanitize input to prevent XSS and control characters
const sanitizeInput = (input) => {
  if (!input || typeof input !== "string") return "";

  return (
    input
      // strip script tags (defense-in-depth)
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      // allow letters, numbers, whitespace, colon and hyphen
      // eslint-disable-next-line
      .replace(/[^\p{L}\p{N}\s:\-]/gu, "")
      .substring(0, 100)
      .trim()
  );
};

function Header() {
  const [query, setQuery] = useState("");
  const [partial, setPartial] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");
  const [bookFilter, setBookFilter] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (query.trim() === "") {
      setResults([]);
      setHasSearched(false);
      setOffset(0);
      setHasMore(false);
      setError("");
    }
  }, [query]);

  const abortControllerRef = useRef(null);
  const searchInputRef = useRef(null);
  const LIMIT = 20;

  const apiClient = useMemo(() => {
    const client = axios.create({
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    client.interceptors.request.use((config) => {
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      return config;
    });

    client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.code === "ECONNABORTED") {
          throw new Error("Busqueda fallo - por favor intenta de nuevo");
        }
        throw error;
      }
    );

    return client;
  }, []);

  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const performSearch = useCallback(
    async (searchQuery, searchOffset = 0, append = false) => {
      const sanitizedQuery = sanitizeInput(searchQuery);

      if (!sanitizedQuery || sanitizedQuery.length < 2) {
        setError("Por favor escribe por lo menos dos caracteres");
        return;
      }

      cleanup();
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError("");

      try {
        const response = await apiClient.get(`${API_URL}/api/search/`, {
          params: {
            q: sanitizedQuery,
            partial: partial.toString(),
            offset: searchOffset,
            limit: LIMIT,
            book: bookFilter || undefined,
          },
          signal: abortControllerRef.current.signal,
        });

        const { results: newResults, has_more, total_returned } = response.data;

        if (append) {
          setResults((prev) => [...prev, ...newResults]);
        } else {
          setResults(newResults);
        }

        setHasMore(has_more || false);
        setOffset(searchOffset + total_returned);
      } catch (error) {
        if (error.name === "AbortError") return;

        console.error("Search error:", error);

        if (error.response?.status === 429) {
          setError("Demaciadas busquedas - Por favor espera un momento");
        } else if (error.response?.status >= 500) {
          setError("Servicio no disponible temporalmente - Trata más tarde");
        } else {
          setError(error.message || "Ocurrió un error en la búsqueda");
        }

        if (!append) {
          setResults([]);
          setHasMore(false);
        }
      } finally {
        setLoading(false);
      }
    },
    [partial, bookFilter, apiClient, cleanup]
  );

  const handleSearch = useCallback(
    (e) => {
      e.preventDefault();
      setHasSearched(true);
      if (!query.trim()) return;

      performSearch(query, 0, false);
    },
    [query, performSearch]
  );

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    performSearch(query, offset, true);
  }, [loading, hasMore, query, offset, performSearch]);

  const handleInputChange = useCallback((e) => {
    setQuery(e.target.value);
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return (
    <main className="font-bembo text-lg flex flex-1 w-full flex-col items-center justify-center text-center px-8 sm:mt-28 mt-20">
      <br />
      <h1 className="mx-auto max-w-4xl font-display text-5xl font-bold tracking-normal text-slate-900 sm:text-7xl mb-10">
        {done ? (
          "BUSCADOR"
        ) : (
          <Typewriter
            words={["BUSCADOR"]}
            loop={1}
            cursor
            cursorStyle="|"
            typeSpeed={80}
            deleteSpeed={40}
            delaySpeed={1000}
            onLoopDone={() => setDone(true)}
          />
        )}
      </h1>

      <form
        onSubmit={handleSearch}
        className="w-full max-w-2xl flex flex-col gap-4 mb-6"
        role="search"
      >
        <div className="relative">
          <input
            id="search-query"
            name="search-query"
            ref={searchInputRef}
            type="text"
            placeholder="Busca: Palabra Clave, Libro, Capítulo, Versículo, etc."
            value={query}
            onChange={handleInputChange}
            className="w-full p-3 pr-10 border border-gray-300 text-lg rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            maxLength={100}
            autoComplete="off"
            aria-label="Buscar en la Biblia"
            disabled={loading}
          />

          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setResults([]);
                setHasSearched(false);
                setError("");
                setOffset(0);
                setHasMore(false);
                searchInputRef.current?.focus();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label="Limpiar búsqueda"
            >
              &#10005;
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4">
          <label
            htmlFor="partial-search"
            className="flex items-center gap-2 cursor-pointer"
          >
            <input
              id="partial-search"
              name="partial-search"
              type="checkbox"
              checked={partial}
              onChange={() => setPartial(!partial)}
              className="rounded focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
            <span>Coincidencia parcial</span>
          </label>

          <select
            id="book-filter"
            name="book-filter"
            value={bookFilter}
            onChange={(e) => setBookFilter(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[170px]"
            disabled={loading}
            aria-label="Filtrar por libro"
          >
            <option value="">Todos los libros</option>
            <option value="Génesis">Génesis</option>
            <option value="Éxodo">Éxodo</option>
            <option value="Levítico">Levítico</option>
            <option value="Números">Números</option>
            <option value="Deuteronomio">Deuteronomio</option>
            <option value="Josué">Josué</option>
            <option value="Jueces">Jueces</option>
            <option value="Rut">Rut</option>
            <option value="1 Samuel">1 Samuel</option>
            <option value="2 Samuel">2 Samuel</option>
            <option value="1 Reyes">1 Reyes</option>
            <option value="2 Reyes">2 Reyes</option>
            <option value="1 Crónicas">1 Crónicas</option>
            <option value="2 Crónicas">2 Crónicas</option>
            <option value="Esdras">Esdras</option>
            <option value="Nehemías">Nehemías</option>
            <option value="Ester">Ester</option>
            <option value="Job">Job</option>
            <option value="Salmos">Salmos</option>
            <option value="Proverbios">Proverbios</option>
            <option value="Eclesiastés">Eclesiastés</option>
            <option value="Cantares">Cantares</option>
            <option value="Isaías">Isaías</option>
            <option value="Jeremías">Jeremías</option>
            <option value="Lamentaciones">Lamentaciones</option>
            <option value="Ezequiel">Ezequiel</option>
            <option value="Daniel">Daniel</option>
            <option value="Oseas">Oseas</option>
            <option value="Joel">Joel</option>
            <option value="Amós">Amós</option>
            <option value="Abdías">Abdías</option>
            <option value="Jonás">Jonás</option>
            <option value="Miqueas">Miqueas</option>
            <option value="Nahúm">Nahúm</option>
            <option value="Habacuc">Habacuc</option>
            <option value="Sofonías">Sofonías</option>
            <option value="Hageo">Hageo</option>
            <option value="Zacarías">Zacarías</option>
            <option value="Malaquías">Malaquías</option>
            <option value="Mateo">Mateo</option>
            <option value="Marcos">Marcos</option>
            <option value="Lucas">Lucas</option>
            <option value="Juan">Juan</option>
            <option value="Hechos">Hechos</option>
            <option value="Romanos">Romanos</option>
            <option value="1 Corintios">1 Corintios</option>
            <option value="2 Corintios">2 Corintios</option>
            <option value="Gálatas">Gálatas</option>
            <option value="Efesios">Efesios</option>
            <option value="Filipenses">Filipenses</option>
            <option value="Colosenses">Colosenses</option>
            <option value="1 Tesalonicenses">1 Tesalonicenses</option>
            <option value="2 Tesalonicenses">2 Tesalonicenses</option>
            <option value="1 Timoteo">1 Timoteo</option>
            <option value="2 Timoteo">2 Timoteo</option>
            <option value="Tito">Tito</option>
            <option value="Filemón">Filemón</option>
            <option value="Hebreos">Hebreos</option>
            <option value="Santiago">Santiago</option>
            <option value="1 Pedro">1 Pedro</option>
            <option value="2 Pedro">2 Pedro</option>
            <option value="1 Juan">1 Juan</option>
            <option value="2 Juan">2 Juan</option>
            <option value="3 Juan">3 Juan</option>
            <option value="Judas">Judas</option>
            <option value="Apocalipsis">Apocalipsis</option>
          </select>

          <button
            type="submit"
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              loading
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-gray-600 hover:bg-gray-700 focus:bg-gray-700"
            } text-white focus:outline-none focus:ring-2 focus:ring-gray-500`}
            disabled={loading || !query.trim()}
            aria-label="Buscar"
          >
            {loading ? "Buscando..." : "Buscar"}
          </button>
        </div>
      </form>

      {hasSearched && error && (
        <div
          className="w-full max-w-2xl mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md"
          role="alert"
        >
          {error}
        </div>
      )}

      {loading && !results.length && (
        <div
          className="flex items-center gap-2 text-gray-500 mb-4"
          aria-live="polite"
        >
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
          <span>Cargando...</span>
        </div>
      )}

      {results.length > 0 && (
        <div
          className="w-full max-w-4xl space-y-4 text-left"
          aria-live="polite"
        >
          <div className="text-lg text-gray-600 mb-4">
            {results.length} resultado{results.length !== 1 ? "s" : ""}
            {hasMore && " (mostrando los primeros)"}
          </div>

          {results.map((verse, idx) => (
            <article
              key={`${verse.book}-${verse.chapter}-${verse.verse}-${idx}`}
              className="border-b border-gray-200 pb-4 last:border-b-0"
            >
              <header className="text-lg font-semibold text-blue-600 mb-1">
                {verse.reference ||
                  `${verse.book} ${verse.chapter}:${verse.verse}`}
              </header>
              <div
                className="text-base leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: verse.highlighted_text || verse.text,
                }}
              />
            </article>
          ))}

          {hasMore && (
            <div className="flex justify-center mt-6">
              <button
                type="button"
                onClick={loadMore}
                disabled={loading}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gray-600 hover:bg-gray-700 focus:bg-gray-700"
                } text-white focus:outline-none focus:ring-2 focus:ring-gray-500`}
                aria-label="Cargar más resultados"
              >
                {loading ? "Cargando..." : "Cargar más"}
              </button>
            </div>
          )}
          <br />
          <br />
          <br />
        </div>
      )}

      {hasSearched && results.length === 0 && !loading && query && !error && (
        <div
          className="text-center text-gray-500 py-8 text-lg"
          role="status"
          aria-live="polite"
        >
          <p className="text-xl mb-2">No se encontraron resultados</p>
          <p className="text-xl">
            Intenta con términos diferentes o usa la búsqueda parcial
          </p>
        </div>
      )}
    </main>
  );
}

export default Header;
